import { Injectable } from '@angular/core';
import { NgbModal }   from '@ng-bootstrap/ng-bootstrap';
import { UIRouter }   from '@uirouter/core';

import { ApiService }               from '../api/api.service';
import { InvoiceAnalysisComponent } from './invoice.analysis.component';
import { InvoiceModel }             from './models/invoice';
import { InvoiceSmsComponent }      from './invoice.sms.component';
import { JsonService }              from '../core/services/json.service';
import { MessagesService }          from '../messages/messages.service';
import { MessengerSmsParamsModel }  from '../messages/messenger/typings/messenger.sms-params';
import { PruneService }             from '../core/services/prune.service';
import { TaxGroupModel }            from '../companies/models/tax-group';

@Injectable({
  providedIn: 'root'
})
export class InvoiceService {
  private _statuses = {
    open     : 'O',
    closed   : 'C',
    processed: 'P'
  };

  private _types = {
    credit  : 'C',
    quote   : 'Q',
    invoice : 'I',
    internal: 'U'
  };

  constructor (
    private apiService     : ApiService,
    private jsonService    : JsonService,
    private messagesService: MessagesService,
    private modalService   : NgbModal,
    private pruneService   : PruneService,
    private router         : UIRouter
  ) {}

  private _isEmpty ( form : ng.IFormController ) {
    for (var key in form) {
      if (form.hasOwnProperty(key)) {
        return false;
      }
    }

    return true;
  }

  assemble ( invoice : InvoiceModel, taxGroups : Array<TaxGroupModel> ) : Promise<InvoiceModel> {
    invoice = this.jsonService.copy(invoice);

    invoice.invoice_items_attributes     = this.pruneService.removeUntouched(invoice.invoice_items_attributes);
    invoice.invoice_items_attributes     = invoice.invoice_items_attributes.concat(invoice.itemsToDelete);
    invoice.customer_deposits_attributes = invoice.customer_deposits_attributes.concat(invoice.depositsToDelete);

    return Promise.resolve()
    .then(() => ( !taxGroups || !taxGroups.length )
      ? this.apiService.get('/tax_groups')
      .then(( response : Array<TaxGroupModel> ) => {
        taxGroups = response;

        return invoice;
      })
      : Promise.resolve(invoice)
    )
    .then(( invoice : InvoiceModel ) => {
      if (invoice.tax_group_id) {
        invoice.tax_group_name = taxGroups.length
          ? taxGroups.filter(group => group.id === invoice.tax_group_id)[0].name
          : invoice.tax_group_name;
      }

      return Promise.resolve(invoice);
    });
  }

  cleanup ( invoice : InvoiceModel, form : ng.IFormController, options : { skipRefresh?: boolean } = {} ) : Promise<InvoiceModel> {
    // Set the form to pristine so page changes can happen without warning
    this._isEmpty(form) ? null : form.$setPristine();

    if (!options.skipRefresh) {

      // Redirect to page with new id from invoice
      this.router.stateService.go('app.invoice', {
        customer_id: invoice.customer_id,
        vehicle_id : invoice.vehicle_id,
        id         : invoice.id
      }, { reload: true });
    }

    return Promise.resolve(invoice);
  }

  isStatus ( invoice : InvoiceModel, status : string ) : boolean {
    if (status === 'actionable') {
      return (this.isType(invoice, 'credit') && this.isStatus(invoice, 'processed')) ||
        invoice.customer_id && this.isType(invoice, 'quote') ||
        (invoice.customer_id && (invoice.repco || invoice.ashdown || invoice.prolink) );
    }

    return this._statuses[status] === invoice.invoice_status;
  }

  isType ( invoice : InvoiceModel, type : string ) : boolean {
    return this._types[type] === invoice.invoice_type;
  }

  save ( invoice : InvoiceModel, taxGroups ?: Array<TaxGroupModel>, form ?: ng.IFormController, options : { skipRefresh?: boolean } = {} ) : Promise<InvoiceModel> {
    const url      = invoice.id ? '/invoice': 'invoices';
    const method   = invoice.id ? 'patch': 'post';

    return this.validate(invoice, form)
    .then(() => this.assemble(invoice, taxGroups))
    .then(( invoice : InvoiceModel ) => this.apiService[method](url, invoice, 'invoice'))
    .then(( invoice : InvoiceModel ) => this.cleanup(invoice, form, options));
  }

  sendSms ( invoice : InvoiceModel, sms : MessengerSmsParamsModel ) : ng.IPromise<any> {
    const modal = this.modalService.open(InvoiceSmsComponent);

    modal.componentInstance.invoice = invoice;
    modal.componentInstance.sms     = sms;

    return modal.result;
  }

  validate ( invoice : InvoiceModel, form : ng.IFormController ) : Promise<InvoiceModel> {
    if ( this._isEmpty(form) ) {
      return Promise.resolve(invoice);
    }

    if ( form.$valid ) {
      return Promise.resolve(invoice);
    }
    else {
      this.messagesService.clientError(form);

      return Promise.reject();
    }
  }

  viewAnalysis ( invoice : InvoiceModel ) : void {
    const modal = this.modalService.open(InvoiceAnalysisComponent, {
      windowClass: 'x-lg-modal'
    });

    modal.componentInstance.invoice = invoice;
  }
}
